import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import type { QuickFillRowMode } from '../../../shared/types';
import type { ValidationQuickFillFocus } from '../../../shared/types/validationNavigation.types';
import type { TableInitialPreset } from '../../../shared/ui/table/hooks/useTableFilters';
import { perf } from '../../../shared/lib/perfLogger';

export interface ValidationScopeInfo {
  requestId: string;
  title: string;
  summary: string;
  affectedCount: number;
  affectedItems: string[];
}

export interface UseQuickFillValidationFocusOptions {
  connectorDefaultRowMode: QuickFillRowMode;
  setRowMode: (
    rowMode: QuickFillRowMode | ((prev: QuickFillRowMode) => QuickFillRowMode)
  ) => void;
  manualRowModeOverrideRef: React.MutableRefObject<boolean>;
  consumedValidationFocusRequestIdRef: React.MutableRefObject<string | null>;
  lastValidationFocusWaitStateRef: React.MutableRefObject<string | null>;
  replaceSelectedRows: (rows: number[]) => void;
  setNavigationMarkerRows: (
    rows: number[] | ((prev: number[]) => number[])
  ) => void;
  setNavigationRequest: (
    request: { rowIndex: number; requestId: number; columnLabel?: string } | null
  ) => void;
  setValidationFocusPreset: (preset: TableInitialPreset | null) => void;
}

export interface UseQuickFillValidationFocusResult {
  activeValidationQuickFillFocus: ValidationQuickFillFocus | null;
  setActiveValidationQuickFillFocus: React.Dispatch<React.SetStateAction<ValidationQuickFillFocus | null>>;
  validationScopeInfo: ValidationScopeInfo | null;
  setValidationScopeInfo: React.Dispatch<React.SetStateAction<ValidationScopeInfo | null>>;
  effectiveValidationScope: ValidationQuickFillFocus | null;
  handleDismissValidationScope: () => void;
  // Owned here: this hook is the validation-navigation domain's store boundary.
  clearIssueNavigationContext: () => void;
}

const buildQuickFillHandoffResetPreset = (requestId: string): TableInitialPreset => ({
  requestId: `${requestId}:handoff-reset`,
  exactNameFilters: [],
});

export function useQuickFillValidationFocus(
  options: UseQuickFillValidationFocusOptions
): UseQuickFillValidationFocusResult {
  const {
    connectorDefaultRowMode,
    setRowMode,
    manualRowModeOverrideRef,
    consumedValidationFocusRequestIdRef,
    lastValidationFocusWaitStateRef,
    replaceSelectedRows,
    setNavigationMarkerRows,
    setNavigationRequest,
    setValidationFocusPreset,
  } = options;

  const issueNavigationContext = usePcfValidationStore((state) => state.issueNavigationContext);
  const clearIssueNavigationContext = usePcfValidationStore((state) => state.clearIssueNavigationContext);

  const incomingValidationQuickFillFocus =
    issueNavigationContext?.targetPage === 'quick-fill'
      ? issueNavigationContext.quickFillFocus ?? issueNavigationContext.actionParams?.quickFillFocus ?? null
      : null;

  const [activeValidationQuickFillFocus, setActiveValidationQuickFillFocus] =
    useState<ValidationQuickFillFocus | null>(null);
  const [validationScopeInfo, setValidationScopeInfo] = useState<ValidationScopeInfo | null>(null);

  const effectiveValidationScope = activeValidationQuickFillFocus ?? incomingValidationQuickFillFocus;

  useEffect(() => {
    if (
      !incomingValidationQuickFillFocus?.requestId ||
      activeValidationQuickFillFocus?.requestId === incomingValidationQuickFillFocus.requestId
    ) {
      return;
    }

    perf.log('QF-Handoff', 'Received validation focus', {
      requestId: incomingValidationQuickFillFocus.requestId,
      labels: incomingValidationQuickFillFocus.labels,
      elementIds: incomingValidationQuickFillFocus.elementIds,
      familyTypeKeys: incomingValidationQuickFillFocus.familyTypeKeys,
      rowCandidates: incomingValidationQuickFillFocus.rowCandidates,
      autofillMode: incomingValidationQuickFillFocus.autofillMode,
      preferredRowMode: incomingValidationQuickFillFocus.preferredRowMode,
    });

    if (issueNavigationContext?.targetPage === 'quick-fill') {
      setValidationScopeInfo({
        requestId: incomingValidationQuickFillFocus.requestId,
        title: issueNavigationContext.title,
        summary: issueNavigationContext.summary,
        affectedCount: issueNavigationContext.affectedCount,
        affectedItems: issueNavigationContext.affectedItems,
      });
    }

    setActiveValidationQuickFillFocus(incomingValidationQuickFillFocus);
    manualRowModeOverrideRef.current = false;
    setRowMode(incomingValidationQuickFillFocus.preferredRowMode ?? connectorDefaultRowMode);
    consumedValidationFocusRequestIdRef.current = null;
    lastValidationFocusWaitStateRef.current = null;
    setValidationFocusPreset(buildQuickFillHandoffResetPreset(incomingValidationQuickFillFocus.requestId));
    replaceSelectedRows([]);
    setNavigationMarkerRows([]);
    setNavigationRequest(null);
  }, [
    activeValidationQuickFillFocus?.requestId,
    connectorDefaultRowMode,
    incomingValidationQuickFillFocus,
    issueNavigationContext,
    manualRowModeOverrideRef,
    consumedValidationFocusRequestIdRef,
    lastValidationFocusWaitStateRef,
    replaceSelectedRows,
    setNavigationMarkerRows,
    setNavigationRequest,
    setRowMode,
    setValidationFocusPreset,
  ]);

  const handleDismissValidationScope = useCallback(() => {
    perf.log('QF-Handoff', 'Validation scope dismissed by user', {
      requestId: validationScopeInfo?.requestId || null,
    });
    setActiveValidationQuickFillFocus(null);
    setValidationScopeInfo(null);
    setValidationFocusPreset(null);
    replaceSelectedRows([]);
    setNavigationMarkerRows([]);
    setNavigationRequest(null);
    manualRowModeOverrideRef.current = false;
    clearIssueNavigationContext();
  }, [
    clearIssueNavigationContext,
    manualRowModeOverrideRef,
    replaceSelectedRows,
    setNavigationMarkerRows,
    setNavigationRequest,
    setValidationFocusPreset,
    validationScopeInfo?.requestId,
  ]);

  return useMemo(
    () => ({
      activeValidationQuickFillFocus,
      setActiveValidationQuickFillFocus,
      validationScopeInfo,
      setValidationScopeInfo,
      effectiveValidationScope,
      handleDismissValidationScope,
      clearIssueNavigationContext,
    }),
    [
      activeValidationQuickFillFocus,
      clearIssueNavigationContext,
      effectiveValidationScope,
      handleDismissValidationScope,
      setActiveValidationQuickFillFocus,
      setValidationScopeInfo,
      validationScopeInfo,
    ]
  );
}
